// The plan catalog behind the Plans page, the upgrade popup and the upgrade banners: limits, prices, trials,
// which features each plan includes, and what each plan offers this store right now. No React in here.
import { hasAccess } from "../../utils/FeatureAccess";
import { GetPlanName, GetPlanDisplayName, SubscriptionPlan, DiscountKind } from "./SubscriptionPlan";

export const planFeatures = {
  [SubscriptionPlan.Starter]: { trialDays: 0, price: 0, maxCampaigns: 2, maxActiveDiscounts: 30 },
  [SubscriptionPlan.Lite]: { trialDays: 14, price: 9.99, maxCampaigns: 10, maxActiveDiscounts: 300 },
  [SubscriptionPlan.Professional]: { trialDays: 14, price: 17.99, maxCampaigns: 30, maxActiveDiscounts: 1500 },
  [SubscriptionPlan.Elite]: { trialDays: 14, price: 25.99, maxCampaigns: 100, maxActiveDiscounts: 7500 },
  [SubscriptionPlan.Unlimited]: { trialDays: 0, price: 49.99, maxCampaigns: 0, maxActiveDiscounts: 0 },
  // Legacy plans
  [SubscriptionPlan.LegacyStarter]: { price: 0, maxCampaigns: 1000, maxActiveDiscounts: 30 },
  [SubscriptionPlan.LegacyBuilder]: { price: 9.99, maxCampaigns: 1000, maxActiveDiscounts: 500 },
  [SubscriptionPlan.LegacyGrower]: { price: 14.99, maxCampaigns: 1000, maxActiveDiscounts: 2500 },
  [SubscriptionPlan.LegacyScaler]: { price: 19.99, maxCampaigns: 1000, maxActiveDiscounts: 7500 },
};

// Length of the free trial a plan offers (0 = no trial). Mirrors ChangeSubscriptionHandler in the backend.
export const getTrialDays = (plan) => planFeatures[plan]?.trialDays || 0;

export const isTrialUsed = (plan, trialUsedForPlans) => trialUsedForPlans?.[GetPlanName(plan)] === true;

// True when the plan offers a trial that the store has not already used.
export const isTrialAvailable = (plan, trialUsedForPlans) =>
  getTrialDays(plan) > 0 && !isTrialUsed(plan, trialUsedForPlans);

// The plans on offer, cheapest first.
export const PLANS = [
  SubscriptionPlan.Starter,
  SubscriptionPlan.Lite,
  SubscriptionPlan.Professional,
  SubscriptionPlan.Elite,
  SubscriptionPlan.Unlimited,
];

// The plan the page pushes: a dark, raised card with this label, and the target of the upgrade banners
// whenever it fixes the store's limit. Only stores below it see it. Switch the label to "Most popular" once
// Business clearly leads paid subscriptions (not trial starts) over a recent month, on enough volume and not
// driven by one discount code; switch back if Professional retakes the lead.
export const RECOMMENDED = { plan: SubscriptionPlan.Elite, label: "Recommended" };

// Who each plan is for (new → small → established → large), with the recommended plan on "established", the
// self-image most paying merchants hold. Each line also says what the plan does for that store.
export const TAGLINES = {
  [SubscriptionPlan.Lite]: "Just getting started",
  [SubscriptionPlan.Professional]: "Big-store sales for a small shop",
  [SubscriptionPlan.Elite]: "Scale up an established store",
  [SubscriptionPlan.Unlimited]: "No ceilings for large stores",
};

const everyPlan = () => true;
// What the app actually locks (FeatureAccess), so the page can't promise more than the plan unlocks.
const gatedBy = (feature) => (plan) => !!hasAccess(feature, plan);
// Listed from a plan up, without a lock in the app.
const fromPlan = (min) => (plan) => PLANS.indexOf(plan) >= PLANS.indexOf(min);

// `card` also lists the feature on the plan cards (the comparison table shows all of them), under `cardLabel`
// when the card needs a shorter name.
const FEATURE_GROUPS = [
  {
    title: "Campaigns & discounts",
    features: [
      { label: "Scheduled start and end", hint: "Sales switch on and off by themselves.", on: everyPlan, card: true },
      { label: "Pick products by filters", hint: "Collections, tags, vendors or types instead of one by one.", on: everyPlan, card: true },
      { label: "Sale price on products & collections", hint: "The real Shopify price changes, so cart and checkout match.", on: everyPlan, card: true },
      { label: "Exclude products or collections", hint: "Leave specific products or collections out of a sale.", on: gatedBy("excludeProducts"), card: true },
      { label: "Mixed discounts in one campaign", hint: "Different products, different discounts, one campaign.", on: gatedBy("differentDiscounts"), card: true },
      { label: "Percent off, amount off or a set price", on: everyPlan },
      { label: "Price rounding", hint: "E.g. end every sale price in .99.", on: everyPlan },
      { label: "Discount from the compare-at price", hint: "Base the sale price on the compare-at price instead of the current price.", on: everyPlan },
      { label: "Choose the price after the sale", hint: "Go back to the old price or to the compare-at price.", on: everyPlan },
      { label: "Block discount codes on sale products", cardLabel: "Block discount codes", on: gatedBy("blockDiscountCodes"), card: true },
    ],
  },
  {
    title: "Storefront widgets",
    features: [
      { label: "Countdown timer on product pages", cardLabel: "Countdown timers", on: everyPlan, card: true },
      { label: "Announcement bars", hint: "A bar across your store announcing the sale.", on: fromPlan(SubscriptionPlan.Lite), card: true },
      { label: "Countdown in announcement bar", on: gatedBy("advancedAnnouncement") },
      { label: "Sale badges", hint: "Badges like “-20%” on product images.", on: gatedBy("saleBadges"), card: true },
      { label: "Hero banners", hint: "A large promotional banner for the sale.", on: gatedBy("heroBanner"), card: true },
      { label: "Flash sale and coming-soon pages", hint: "One page listing every product in the sale, one teasing it before it starts.", on: gatedBy("flashSalePage"), card: true },
    ],
  },
  {
    title: "Automation",
    features: [
      { label: "Auto-tag products while on sale", hint: "The tag comes off when the sale ends.", on: gatedBy("autoTagProducts"), card: true },
      { label: "Sale collection", hint: "A collection of the sale's products, for sales under 1,500 products.", on: everyPlan },
    ],
  },
];

// Within each group, features on more plans come first, so every card and table column reads as a staircase
// with the most exclusive features at the bottom. Ties keep the order above.
const reach = (feature) => PLANS.filter((plan) => feature.on(plan)).length;
export const FEATURES = FEATURE_GROUPS.map((group) => ({
  ...group,
  features: [...group.features].sort((a, b) => reach(b) - reach(a)),
}));

export const formatCount = (n) => (n === 0 ? "Unlimited" : n.toLocaleString("en-US"));
export const formatPrice = (n) => `$${Number(n).toFixed(2)}`;
// "$3" for whole dollars, like the Halloween Offer's first months; otherwise with cents.
export const formatDollars = (n) => (Number.isInteger(Number(n)) ? `$${Number(n)}` : formatPrice(n));
// The backend's Math.Round(decimal, 2): to the cent, with halves going to the even cent. Counted in cents, because
// in floating point 17.99 × 50% is 8.99499… and would round down where the backend's exact 8.995 rounds up.
const roundCents = (cents) => {
  const floor = Math.floor(cents);
  if (Math.abs(cents - floor - 0.5) > 1e-9) return Math.round(cents);
  return floor % 2 === 0 ? floor : floor + 1;
};
const toCents = (dollars) => Math.round(dollars * 100);
const percentOffCents = (price, percent) => roundCents((toCents(price) * percent) / 100);
// The price after taking `offCents` off, never below zero. Mirrors ChangeSubscriptionHandler.
const priceAfter = (price, offCents) => Math.max(0, toCents(price) - offCents) / 100;

// Everything the page needs to show one plan to this store. Mirrors ChangeSubscriptionHandler: the Halloween
// Offer wins on the plans it covers, then a valid discount code, otherwise 25% off the first month while the
// plan's trial is unused.
// `discount` is { code, kind, value, durationMonths, appliesToPlans } for an active code, or null.
// `halloweenOffer` is the subscription meta's HalloweenOffer, which the backend sends only to eligible stores.
export function getPlanOffer(plan, { currentPlan, currentPlanPrice = null, currentPlanPriceLoading = false, trialUsedForPlans, discount = null, halloweenOffer = null, isReverseCharge = false, activeDiscounts = 0, createdCampaigns = 0 }) {
  const limits = planFeatures[plan];
  const isCurrent = plan === currentPlan;
  // Stores that subscribed before a price change keep paying their old price.
  const price = isCurrent && currentPlanPrice != null ? currentPlanPrice : limits.price;

  // The backend charges the offer's first-month price after the offer's trial, whatever code was entered.
  const halloween = isCurrent ? null : halloweenOffer?.Plans?.[GetPlanName(plan)];

  const applies = (discount?.appliesToPlans || []).map(GetPlanName);
  const codeApplies = !halloween && !!discount && !isCurrent && price > 0 && (applies.length === 0 || applies.includes(GetPlanName(plan)));

  let promo = null;
  if (halloween) {
    promo = { halloween: true, months: 1, price: halloween.FirstMonthPrice };
  } else if (codeApplies) {
    const off = discount.kind === DiscountKind.Percent ? `${discount.value}%` : `$${discount.value}`;
    const offCents = discount.kind === DiscountKind.Percent ? percentOffCents(price, discount.value) : toCents(discount.value);
    promo = { off, months: discount.durationMonths || 1, price: priceAfter(price, offCents) };
  } else if (!isCurrent && price > 0 && !isTrialUsed(plan, trialUsedForPlans)) {
    promo = { off: "25%", months: 1, price: priceAfter(price, percentOffCents(price, 25)) };
  }

  return {
    plan,
    name: GetPlanDisplayName(plan),
    tagline: TAGLINES[plan],
    isCurrent,
    isDowngrade: !isCurrent && plan < currentPlan,
    // A nudge, so only stores below the plan see it.
    recommended: plan === RECOMMENDED.plan && plan > currentPlan,
    price,
    priceLoading: isCurrent && currentPlanPriceLoading && limits.price > 0,
    trialDays: halloween ? halloween.TrialDays : !isCurrent && isTrialAvailable(plan, trialUsedForPlans) ? getTrialDays(plan) : 0,
    promo,
    codeExcluded: !halloween && !!discount && !isCurrent && price > 0 && !codeApplies,
    maxDiscounts: limits.maxActiveDiscounts,
    maxCampaigns: limits.maxCampaigns,
    fitsUsage:
      (limits.maxActiveDiscounts === 0 || activeDiscounts <= limits.maxActiveDiscounts) &&
      (limits.maxCampaigns === 0 || createdCampaigns <= limits.maxCampaigns),
    // EU stores with a VAT number are billed net (reverse charge).
    vatNote: isReverseCharge && price > 0,
    url: isCurrent ? undefined : `/subscription/change/${plan}${codeApplies ? `?discount=${encodeURIComponent(discount.code)}` : ""}`,
  };
}

export const getPlanOffers = (ctx) => PLANS.map((plan) => getPlanOffer(plan, ctx));

// Card features the target plan has and the current one doesn't, storefront widgets first (the most visible).
const gainedFeatures = (from, to) =>
  [...FEATURES]
    .sort((a, b) => (b.title === "Storefront widgets") - (a.title === "Storefront widgets"))
    .flatMap((group) => group.features)
    .filter((f) => f.card && f.on(to) && !f.on(from))
    .map((f) => f.cardLabel || f.label);

const amountOf = (n, noun) => `${formatCount(n)} ${noun}`;

// The upgrade nudge when the store has hit a limit, or is about to (`productsInCampaign` are the products of
// the campaign being saved): why, which plan fixes it, and what that plan adds. Null when nothing is in the way
// or no plan fixes it. The target is the recommended plan whenever it fixes the limit, so the banner never
// argues with the plan cards; otherwise the next plan that does.
export function getUpgradeNudge({ currentPlan, createdCampaigns = 0, activeDiscounts = 0, predictedActiveDiscounts = 0, productsInCampaign = 0, trialUsedForPlans, discount = null, halloweenOffer = null }) {
  const limits = planFeatures[currentPlan];
  if (!limits) return null;
  const { maxCampaigns, maxActiveDiscounts: maxDiscounts } = limits;
  const campaignsFull = maxCampaigns > 0 && createdCampaigns >= maxCampaigns;
  const productsFull = maxDiscounts > 0 && activeDiscounts >= maxDiscounts;
  const scheduledOver = maxDiscounts > 0 && predictedActiveDiscounts > maxDiscounts;
  const productsNeeded = Math.max(activeDiscounts + productsInCampaign, predictedActiveDiscounts, productsFull ? activeDiscounts + 1 : 0);
  const productsOver = maxDiscounts > 0 && productsNeeded > maxDiscounts;
  if (!campaignsFull && !productsOver) return null;

  const fits = (o) =>
    (o.maxCampaigns === 0 || o.maxCampaigns > createdCampaigns) && (o.maxDiscounts === 0 || o.maxDiscounts >= productsNeeded);
  const candidates = getPlanOffers({ currentPlan, trialUsedForPlans, discount, halloweenOffer }).filter((o) => o.plan > currentPlan && fits(o));
  const target = candidates.find((o) => o.recommended) || candidates[0];
  if (!target) return null;

  const currentName = GetPlanDisplayName(currentPlan);
  const title =
    productsInCampaign > 0 && productsOver
      ? "This campaign needs a bigger plan"
      : scheduledOver
      ? "Your scheduled sales need a bigger plan"
      : campaignsFull
      ? `You've used all ${formatCount(maxCampaigns)} campaigns on ${currentName}`
      : `You've reached the ${formatCount(maxDiscounts)}-product limit on ${currentName}`;
  const gained = gainedFeatures(currentPlan, target.plan);

  return {
    title,
    target,
    currentName,
    currentIsPaid: limits.price > 0,
    gains: [
      amountOf(target.maxCampaigns, "campaigns"),
      amountOf(target.maxDiscounts, "products on sale"),
      gained.length > 1 ? `${gained[0]} + ${gained.length - 1} more features` : gained[0],
    ].filter(Boolean),
  };
}
